import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, adminApi, formatError } from "@/lib/api";
import { SectionLabel, Headline, Field } from "@/components/Doc";
import { Authorizations, Clients, Runs, StatusEvents } from "@/components/admin/Console";

const SECTIONS = [["register", "Register"], ["authorizations", "Authorizations"], ["clients", "Clients"], ["runs", "Runs"], ["events", "Status events"]];

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();

const Login = ({ onLogin }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login", { password: pw });
      sessionStorage.setItem("lh_admin_token", data.token);
      onLogin();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="max-w-md mx-auto bg-paper border border-carbon p-8 sm:p-10 shadow-[6px_6px_0_0_#D9C9A8]" data-testid="admin-login-form">
      <span className="t-label text-olive">Form LH-99 — Restricted</span>
      <h1 className="t-head text-3xl mt-2">Internal register</h1>
      <div className="mt-8">
        <Field label="Password" name="password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required testId="admin-password" />
      </div>
      <button type="submit" className="btn mt-8 w-full" disabled={busy} data-testid="admin-login-btn">{busy ? "Checking…" : "Open the register"}</button>
    </form>
  );
};

const Submissions = ({ rows, desc, onToggle }) => {
  const [open, setOpen] = useState(null);
  return (
  <div className="overflow-x-auto border border-rule" data-testid="submissions-table">
    <table className="w-full text-left">
      <thead className="bg-manila">
        <tr className="t-label text-olive">
          <th className="px-4 py-3 font-normal">Date</th>
          <th className="px-4 py-3 font-normal">Name</th>
          <th className="px-4 py-3 font-normal">Company</th>
          <th className="px-4 py-3 font-normal">Email</th>
          <th className="px-4 py-3 font-normal">
            <button onClick={onToggle} className="t-label text-carbon hover:text-stamp" data-testid="sort-score-btn">Score {desc ? "↓" : "↑"}</button>
          </th>
          <th className="px-4 py-3 font-normal">Band</th>
          <th className="px-4 py-3 font-normal">Highest module</th>
          <th className="px-4 py-3 font-normal">Answers</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <Fragment key={r.id}>
          <tr className="border-t border-rule t-body text-base" data-testid={`submission-row-${r.id}`}>
            <td className="px-4 py-3 t-mono text-sm whitespace-nowrap">{fmtDate(r.date)}</td>
            <td className="px-4 py-3">{r.name}<div className="t-label text-rule">{r.role}</div></td>
            <td className="px-4 py-3">{r.company}{r.team_code && <div className="t-label text-rule">Team {r.team_code}</div>}</td>
            <td className="px-4 py-3 t-mono text-sm">{r.email}</td>
            <td className="px-4 py-3 t-mono font-bold">{r.total}</td>
            <td className="px-4 py-3"><span className={`t-label ${["EXPOSED", "OPEN"].includes(r.band) ? "text-stamp" : "text-olive"}`}>{r.band}</span></td>
            <td className="px-4 py-3 t-label">{r.top_module}{r.converted && <div className="text-stamp">{r.converted.replace("_", " ")}</div>}</td>
            <td className="px-4 py-3"><button onClick={() => setOpen(open === r.id ? null : r.id)} className="t-label text-carbon hover:text-stamp whitespace-nowrap" data-testid={`toggle-answers-${r.id}`}>{open === r.id ? "Close" : "Read"}</button></td>
          </tr>
          {open === r.id && (
            <tr className="border-t border-rule/50 bg-paper" data-testid={`answers-detail-${r.id}`}>
              <td colSpan={8} className="px-4 py-5">
                <div className="grid md:grid-cols-2 gap-x-10 gap-y-3">
                  {r.answers.map((a) => (
                    <div key={a.id} className="grid grid-cols-[52px_1fr_auto] gap-3 items-start border-b border-rule/40 pb-2">
                      <span className="t-label pt-1">Q {String(a.id).padStart(2, "0")}</span>
                      <div>
                        <p className="t-body text-base leading-snug text-olive">{a.text}</p>
                        <p className="t-body text-base leading-snug mt-0.5"><span className="t-label text-rule mr-2">Answer</span>{a.answer}</p>
                      </div>
                      <span className={`t-mono text-sm font-bold pt-1 ${a.score >= 6 ? "text-stamp" : ""}`}>{a.score}<span className="t-label text-rule ml-2">{a.module}</span></span>
                    </div>
                  ))}
                </div>
              </td>
            </tr>
          )}
          </Fragment>
        ))}
        {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-8 t-body italic text-olive text-center">No submissions filed yet.</td></tr>}
      </tbody>
    </table>
  </div>
  );
};

const Enquiries = ({ rows }) => (
  <div className="overflow-x-auto border border-rule" data-testid="enquiries-table">
    <table className="w-full text-left">
      <thead className="bg-manila">
        <tr className="t-label text-olive">
          {["Date", "Name", "Company", "Email", "Regarding", "Notes"].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-t border-rule t-body text-base align-top">
            <td className="px-4 py-3 t-mono text-sm whitespace-nowrap">{fmtDate(r.created_at)}</td>
            <td className="px-4 py-3">{r.name}<div className="t-label text-rule">{r.role}</div></td>
            <td className="px-4 py-3">{r.company}</td>
            <td className="px-4 py-3 t-mono text-sm">{r.email}</td>
            <td className="px-4 py-3 t-label">{r.interest}</td>
            <td className="px-4 py-3 max-w-md">{r.message}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 t-body italic text-olive text-center">No enquiries filed yet.</td></tr>}
      </tbody>
    </table>
  </div>
);

const PaperRequests = ({ rows }) => (
  <div className="overflow-x-auto border border-rule" data-testid="paper-requests-table">
    <table className="w-full text-left">
      <thead className="bg-manila">
        <tr className="t-label text-olive">
          {["Date", "Name", "Company", "Email", "Sent", "Opened"].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-t border-rule t-body text-base">
            <td className="px-4 py-3 t-mono text-sm whitespace-nowrap">{fmtDate(r.created_at)}</td>
            <td className="px-4 py-3">{r.name}<div className="t-label text-rule">{r.role}</div></td>
            <td className="px-4 py-3">{r.company}</td>
            <td className="px-4 py-3 t-mono text-sm">{r.email}</td>
            <td className="px-4 py-3 t-label">{r.email_sent ? "Yes" : "No"}</td>
            <td className="px-4 py-3 t-mono">{r.downloads}×</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 t-body italic text-olive text-center">No paper requests filed yet.</td></tr>}
      </tbody>
    </table>
  </div>
);

export default function Admin() {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("lh_admin_token"));
  const [tab, setTab] = useState("submissions");
  const [section, setSection] = useState("register");
  const [counts, setCounts] = useState({ flaggedRuns: 0, inReview: 0 });
  const [subs, setSubs] = useState([]);
  const [enqs, setEnqs] = useState([]);
  const [papers, setPapers] = useState([]);
  const [desc, setDesc] = useState(true);

  const logout = () => { sessionStorage.removeItem("lh_admin_token"); setAuthed(false); };

  useEffect(() => {
    if (!authed) return;
    const client = adminApi();
    Promise.all([client.get("/admin/submissions"), client.get("/admin/enquiries"), client.get("/admin/paper-requests"), client.get("/admin/counts")])
      .then(([s, e, p, c]) => { setSubs(s.data); setEnqs(e.data); setPapers(p.data); setCounts(c.data); })
      .catch((err) => { if (err?.response?.status === 401) logout(); else toast.error(formatError(err)); });
  }, [authed]);

  const exportCsv = async () => {
    try {
      const { data } = await adminApi().get("/admin/submissions.csv", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: "longhand-audit-submissions.csv" });
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const sorted = [...subs].sort((a, b) => (desc ? b.total - a.total : a.total - b.total));

  return (
    <main className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 min-h-[70vh]" data-testid="admin-page">
      {!authed ? <Login onLogin={() => setAuthed(true)} /> : (
        <div className="settle">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div>
              <SectionLabel>Form LH-99 — Internal Register</SectionLabel>
              <Headline as="h1" className="mt-6 text-4xl sm:text-5xl">The register.</Headline>
            </div>
            <div className="flex gap-3">
              <button onClick={exportCsv} className="btn" data-testid="export-csv-btn">Export CSV</button>
              <button onClick={logout} className="t-label text-olive hover:text-carbon px-3" data-testid="admin-logout-btn">Close</button>
            </div>
          </div>
          <nav className="flex flex-wrap gap-6 mt-10 border-b border-carbon pb-3" data-testid="admin-sections">
            {SECTIONS.map(([k, label]) => (
              <button key={k} onClick={() => setSection(k)} className={`t-label flex items-center gap-2 ${section === k ? "text-carbon underline underline-offset-4" : "text-olive hover:text-carbon"}`} data-testid={`section-${k}`}>
                {label}
                {k === "authorizations" && counts.inReview > 0 && <span className="t-mono text-[0.65rem] bg-carbon text-paper px-1.5" data-testid="badge-in-review">{counts.inReview}</span>}
                {k === "runs" && counts.flaggedRuns > 0 && <span className="t-mono text-[0.65rem] bg-stamp text-paper px-1.5" data-testid="badge-flagged-runs">{counts.flaggedRuns}</span>}
              </button>
            ))}
          </nav>
          {section === "authorizations" && <div className="mt-8"><Authorizations /></div>}
          {section === "clients" && <div className="mt-8"><Clients /></div>}
          {section === "runs" && <div className="mt-8"><Runs /></div>}
          {section === "events" && <div className="mt-8"><StatusEvents /></div>}
          {section === "register" && <>
          <div className="flex gap-1 mt-8 border-b border-carbon">
            <button onClick={() => setTab("submissions")} className={`folder-tab ${tab === "submissions" ? "active" : ""}`} data-testid="tab-submissions">Audit submissions ({subs.length})</button>
            <button onClick={() => setTab("enquiries")} className={`folder-tab ${tab === "enquiries" ? "active" : ""}`} data-testid="tab-enquiries">Enquiries ({enqs.length})</button>
            <button onClick={() => setTab("papers")} className={`folder-tab ${tab === "papers" ? "active" : ""}`} data-testid="tab-papers">Paper requests ({papers.length})</button>
          </div>
          <div className="mt-8">
            {tab === "submissions" ? <Submissions rows={sorted} desc={desc} onToggle={() => setDesc(!desc)} /> : tab === "enquiries" ? <Enquiries rows={enqs} /> : <PaperRequests rows={papers} />}
          </div>
          </>}
        </div>
      )}
    </main>
  );
}
